import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { getPopupRegistration, openPopup, closePopup, Priority } from 'app/PopupHolder';
import { removeInvestorFromGroup } from 'data/investors';
import CheckboxItem from 'components/Checkbox';
import SVG from 'components/base/SVG';
import { useToast } from '../ToastMessage/ToastManager';

import modalCss from '../AddToMarketingListModal/style.scss';
import css from './style.scss';

const BODY_CLASS_NAME = 'bodyModal';

// "Remove from Investor List?" (ATAI-186, S10 build-spec §7b / Figma node
// 659-96783). Triggered only from an already-saved bookmark on the Investor
// Search card — the Contacts-page removal path (§7a) stays on the existing
// generic drag-and-drop and never opens this modal.
//
// Checkbox semantics per the modal's own copy ("Select the list(s) you'd
// like to remove this investor from"): every list starts UNCHECKED, and
// checking a list marks it for removal. Remove acts only on the lists
// checked.
//
// Removal reuses the existing generic delete endpoint (removeInvestorFromGroup
// -> DELETE user/contacts/new) once per checked list, keyed by `contactId`
// on each savedGroups entry — added to the S2/S3 `savedGroups` DTO by the
// propstream-core fix landing alongside this (S10/ATAI-186 BE). Until that's
// deployed, `investor.savedGroups` entries won't carry `contactId` and Remove
// will fail per-list the same way a stale/deleted list would.
//
// Partial-failure policy (undocumented in the design as of 2026-09-15,
// flagged on ATAI-187/Confluence): lists are removed independently and in
// parallel; whichever succeed are reported to `onRemove` so the caller's
// local state reflects reality even if others failed, and a toast names any
// failures so the user can retry just those.
const RemoveFromInvestorListModal = ({ investor, onRemove, closePopup, onClose }) => {
  const rawGroups = investor?.savedGroups;
  const hasGroupsData = Array.isArray(rawGroups);
  // Guards a real race: the Investor Profile modal renders instantly from
  // the search-result object (whose savedGroups entries never carry
  // contactId), then a slower, separate fetch resolves the richer profile
  // data (contactId included) in the background — see Profile/index.js's
  // merge. Nothing gates the bookmark on that second fetch, so clicking
  // Remove before it resolves hands this modal savedGroups entries with no
  // contactId to act on. Treat that the same as "not loaded yet" rather
  // than rendering checkboxes that would submit `selection: [null]`.
  const groupsReady = hasGroupsData && rawGroups.every((list) => list.contactId != null);
  const lists = groupsReady ? rawGroups : [];
  // Figma node 659-96783: saved-to-exactly-one-list is a plain confirmation
  // ("Are you sure you want to remove this investor from the list
  // '{name}'?"), not a checkbox with nothing meaningful to uncheck — the
  // checkbox layout (with its own intro line) is for two or more lists.
  const isSingleList = lists.length === 1;
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [removing, setRemoving] = useState(false);
  const modalRef = useRef(null);
  const dispatch = useDispatch();
  const showToast = useToast();

  const handleClose = () => {
    document.body.classList.remove(BODY_CLASS_NAME);
    closePopup?.();
    onClose?.();
  };

  useEffect(() => {
    document.body.classList.add(BODY_CLASS_NAME);
    const handleKeyDown = (e) => e.key === 'Escape' && handleClose();

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.classList.remove(BODY_CLASS_NAME);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleList = (groupId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  // Single-list mode has no checkboxes to read — the one list is always the
  // target, and Remove is enabled as soon as the modal has real data.
  const listsToRemove = isSingleList
    ? lists
    : lists.filter((list) => checkedIds.has(list.groupId));

  const handleCancel = () => handleClose();

  // One DELETE per checked list, in parallel — resolves rather than
  // rejects on failure so Promise.all always waits for every call to settle
  // and a single failed list can't abort the ones that succeeded.
  const removeFromList = (list) =>
    new Promise((resolve) => {
      dispatch(
        removeInvestorFromGroup(
          list.groupId,
          list.groupName,
          list.contactId,
          () => resolve({ list, ok: true }),
          () => resolve({ list, ok: false })
        )
      );
    });

  const handleRemove = async () => {
    if (!listsToRemove.length || removing) return;

    setRemoving(true);
    const results = await Promise.all(listsToRemove.map(removeFromList));
    const removed = results.filter((r) => r.ok).map((r) => r.list);
    const failed = results.filter((r) => !r.ok).map((r) => r.list);

    if (failed.length) {
      showToast?.(
        `Could not remove from ${failed.map((f) => f.groupName).join(', ')}. Please try again.`,
        'error'
      );
    }
    if (removed.length) {
      onRemove?.(removed);
    }

    setRemoving(false);
    handleClose();
  };

  return (
    <div className={modalCss.modalOverlay}>
      <div className={modalCss.modalWrapper}>
        <div
          ref={modalRef}
          className={classNames(modalCss.modalBox, css.modalBox, 'modal-content', {
            [css.singleList]: isSingleList,
          })}
        >
          <div className={classNames(modalCss.modal_header, css.header)}>
            <h3 className={modalCss.modal_title}>Remove from Investor List?</h3>
          </div>

          <div className={classNames(modalCss.modal_body, css.body)}>
            {!lists.length ? (
              <p className={css.emptyState}>
                {!hasGroupsData
                  ? 'List details are not available yet.'
                  : !groupsReady
                    ? 'Still loading this investor’s list details — close this and try again in a moment.'
                    : "This investor isn't saved to any Investor List."}
              </p>
            ) : isSingleList ? (
              <p className={css.confirmText}>
                Are you sure to remove this investor from the list {lists[0].groupName}?
              </p>
            ) : (
              <>
                <p className={css.introText}>
                  You’ve saved this investor to {lists.length} Investor Lists. Select the list(s)
                  you’d like to remove this investor from.
                </p>
                <ul className={css.listGroup}>
                  {lists.map((list) => (
                    <li key={list.groupId} className={modalCss.checkbox_row}>
                      <CheckboxItem
                        label={list.groupName}
                        checked={checkedIds.has(list.groupId)}
                        onChange={() => toggleList(list.groupId)}
                        labelClassName={modalCss.checkBoxLabelClassName}
                        boxClassName={css.checkboxBox}
                        checkmarkClassName={css.checkboxMark}
                      />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className={classNames(modalCss.modal_footer, css.footer)}>
            <button
              type="button"
              className={
                isSingleList
                  ? css.btnText
                  : classNames(modalCss.btn, modalCss.btn_secondary, {
                [modalCss.disabledBtn]: removing,
                    })
              }
              onClick={handleCancel}
              disabled={removing}
            >
              Cancel
            </button>

            <button
              type="button"
              className={
                isSingleList
                  ? css.btnRemove
                  : classNames(modalCss.btn, css.btnDanger, {
                      [modalCss.disabledBtn]: !listsToRemove.length || removing,
                    })
              }
              onClick={handleRemove}
              disabled={!listsToRemove.length || removing}
            >
              {removing ? <SVG icon="spinner" className={modalCss.spinner} /> : 'Remove'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const registrationId = getPopupRegistration(RemoveFromInvestorListModal);
RemoveFromInvestorListModal.open = (props = {}) =>
  openPopup(registrationId, { priority: Priority.MEDIUM, ...props });
RemoveFromInvestorListModal.close = () => closePopup({ popup: registrationId });

export default RemoveFromInvestorListModal;
